import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import API from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, Copy, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/** Tiny SVG butterfly with flapping wings. Inline so we don't ship an extra
 *  asset; viewBox matched to a 60×52 box with room for both wings to flap. */
function ButterflySprite({ flap = true }) {
  return (
    <svg width="48" height="41" viewBox="0 0 60 52" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <defs>
        <radialGradient id="bfly-wing" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#fde68a" />
          <stop offset="60%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#9333ea" />
        </radialGradient>
        <radialGradient id="bfly-spot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="30" cy="26" rx="2" ry="14" fill="#1e1b4b" />
      <circle cx="30" cy="13" r="3" fill="#1e1b4b" />
      <path d="M30 11 Q26 6 23 4" stroke="#1e1b4b" strokeWidth="1" strokeLinecap="round" fill="none" />
      <path d="M30 11 Q34 6 37 4" stroke="#1e1b4b" strokeWidth="1" strokeLinecap="round" fill="none" />
      <motion.g
        style={{ transformOrigin: "30px 26px" }}
        animate={flap ? { scaleX: [1, 0.55, 1] } : { scaleX: 1 }}
        transition={{ duration: 0.45, repeat: Infinity, ease: "easeInOut" }}
      >
        <path d="M28 16 Q4 10 6 28 Q8 42 28 36 Z" fill="url(#bfly-wing)" />
        <circle cx="14" cy="22" r="3" fill="url(#bfly-spot)" />
        <circle cx="18" cy="32" r="2" fill="url(#bfly-spot)" />
      </motion.g>
      <motion.g
        style={{ transformOrigin: "30px 26px" }}
        animate={flap ? { scaleX: [1, 0.55, 1] } : { scaleX: 1 }}
        transition={{ duration: 0.45, repeat: Infinity, ease: "easeInOut", delay: 0.02 }}
      >
        <path d="M32 16 Q56 10 54 28 Q52 42 32 36 Z" fill="url(#bfly-wing)" />
        <circle cx="46" cy="22" r="3" fill="url(#bfly-spot)" />
        <circle cx="42" cy="32" r="2" fill="url(#bfly-spot)" />
      </motion.g>
    </svg>
  );
}

/** Floating, click-able butterfly easter egg.
 *  Flow:
 *    1. Hidden until the visitor scrolls down to the Fact-or-Myth section
 *       (opacity ramps with scroll progress so it gradually emerges).
 *    2. Click → shows a celebratory popup with an auto-generated $7-off
 *       stackable single-use code. Works for anonymous + authenticated users.
 *    3. Code is auto-saved server-side; admin sees Generated vs Used stats. */
export default function ButterflyEasterEgg() {
  const reduceMotion = useReducedMotion();
  const { isAuthenticated, user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [copied, setCopied] = useState(false);
  const [scrollOpacity, setScrollOpacity] = useState(0);

  // Lazy figure-8 path across the upper portion of the page.
  const path = useMemo(() => ([
    { x: "8vw",  y: "40vh" },
    { x: "30vw", y: "20vh" },
    { x: "55vw", y: "32vh" },
    { x: "78vw", y: "18vh" },
    { x: "85vw", y: "44vh" },
    { x: "60vw", y: "55vh" },
    { x: "32vw", y: "48vh" },
    { x: "12vw", y: "60vh" },
  ]), []);

  // Pull config on mount — runs ONCE regardless of auth state changes.
  // Note: we deliberately don't gate on a localStorage flag here — the
  // server is the source of truth for "already claimed" (24h rate-limit per
  // IP+UA for anonymous, unique per user_id for patients). Letting the
  // butterfly re-appear on refresh and re-show the SAME code on click is
  // friendlier than a permanent localStorage lockout that leaves visitors
  // stranded if their state gets out of sync.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/butterfly/config");
        if (cancelled) return;
        setEnabled(!!data?.enabled);
      } catch { /* fail-closed */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Server-confirmed prior-claim check for logged-in patients only.
  useEffect(() => {
    if (!isAuthenticated || user?.role !== "patient") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/butterfly/mine");
        if (cancelled) return;
        if (data?.claimed) setAlreadyClaimed(true);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user]);

  // Scroll-tied opacity ramp anchored to the Fact-or-Myth section.
  // **Direction**: opacity = 0 right after the visitor passes the bottom of
  // the Fact-or-Myth section, ramping up to 1 by the bottom of the page.
  // The further down they scroll, the more visible the butterfly becomes —
  // pulling them through the entire landing page to find the easter egg.
  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const target = document.querySelector('[data-testid="fact-or-myth-section"]');
      if (!target) return;
      // Use getBoundingClientRect + scrollY for true PAGE coordinates.
      // `offsetTop` is relative to the offsetParent — when the section is
      // nested inside a positioned ancestor (e.g. the sticky header
      // wrapper), offsetTop returns near-zero and the math instantly
      // saturates to 1.0. getBoundingClientRect doesn't have that issue.
      const rect = target.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const sectionBottomY = rect.bottom + scrollY;
      const docHeight = document.documentElement.scrollHeight;
      const wh = window.innerHeight;
      // Hit 100% one viewport short of the absolute page bottom so the
      // butterfly is visible BEFORE the visitor hits the footer.
      const fullVisibleY = Math.max(sectionBottomY + wh, docHeight - wh);
      const span = fullVisibleY - sectionBottomY;
      if (span <= 0) {
        setScrollOpacity(0);
        return;
      }
      const progress = (scrollY - sectionBottomY) / span;
      setScrollOpacity(Math.max(0, Math.min(1, progress)));
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [enabled]);

  // Early-return guard: if disabled or already claimed AND we don't have a
  // result we're actively showing, render nothing. The `result` check keeps
  // the Dialog mounted after a fresh claim so the popup doesn't disappear
  // the instant we flip `alreadyClaimed` to true.
  if (!enabled || (alreadyClaimed && !result)) return null;

  const onCatch = async () => {
    if (claiming) return;
    setClaiming(true);
    try {
      // Anonymous OR authenticated — same endpoint, the server figures out
      // which path to take. No more redirect; the popup carries the moment.
      const { data } = await API.post("/butterfly/claim");
      // Tiny celebratory buzz on phones. Silent no-op on desktop and on
      // browsers that don't expose the Vibration API (Safari, Firefox iOS).
      try { navigator.vibrate?.(50); } catch { /* noop */ }
      setResult(data);
      setOpen(true);
      // Hide the floating butterfly for the rest of this session so the
      // visitor doesn't keep clicking. Refresh restores it; the server
      // returns their existing code (idempotent) so they can re-view it.
      setAlreadyClaimed(true);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "The butterfly slipped away — try again");
    } finally {
      setClaiming(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.code) return;
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  // Visibility = scroll progress (or 1 on reduced-motion since they don't
  // see the path animation drawing the eye anyway). Tiny minimum so click-
  // hit-area never literally goes to zero before it's clearly hidden.
  const baseOpacity = reduceMotion ? Math.max(scrollOpacity, scrollOpacity > 0 ? 1 : 0) : scrollOpacity;

  return (
    <>
      {!alreadyClaimed && (
        <motion.button
        type="button"
        aria-label="Catch the butterfly for a surprise"
        title="Catch me!"
        onClick={onCatch}
        className="fixed z-[110] cursor-grab active:cursor-grabbing focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-full"
        style={{
          filter: "drop-shadow(0 6px 14px rgba(147,51,234,0.35))",
          opacity: baseOpacity,
          // Don't intercept pointer events while the butterfly is mostly
          // invisible — keeps the cursor clean during early scroll.
          pointerEvents: baseOpacity < 0.15 ? "none" : "auto",
        }}
        initial={{ x: path[0].x, y: path[0].y }}
        animate={
          reduceMotion
            ? { x: path[0].x, y: path[0].y }
            : {
                x: path.map((p) => p.x),
                y: path.map((p) => p.y),
                rotate: [0, 6, -6, 4, -4, 6, -3, 2],
              }
        }
        transition={
          reduceMotion
            ? { duration: 0 }
            : {
                duration: 28,
                repeat: Infinity,
                ease: "easeInOut",
                times: path.map((_, i) => i / (path.length - 1)),
              }
        }
        whileHover={{ scale: 1.12 }}
        whileTap={{ scale: 0.92 }}
        data-testid="butterfly-easter-egg"
      >
        <ButterflySprite flap={!reduceMotion} />
      </motion.button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md overflow-hidden">
          <div className="relative -mx-6 -mt-6 mb-2 px-6 py-7 bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-500 text-white">
            <span aria-hidden className="absolute top-3 left-6 text-white/90 text-sm">✦</span>
            <span aria-hidden className="absolute top-6 right-8 text-white/80 text-xs">✧</span>
            <span aria-hidden className="absolute bottom-3 left-12 text-white/80 text-xs">✦</span>
            <DialogHeader>
              <DialogTitle className="text-white text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Sparkles className="w-5 h-5 text-amber-200" />
                Congrats — you found me!
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-white/90 mt-1">
              Thanks for being interactive. Here&apos;s <strong className="text-amber-100">${result?.amount || 7} off</strong> on us — stackable, single-use, yours.
            </p>
          </div>

          <div className="px-1 pb-2">
            <div className="rounded-2xl border-2 border-dashed border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-950/40 p-5 text-center">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-purple-700 dark:text-purple-300">Your code</p>
              <p className="text-2xl font-bold font-mono mt-1.5 text-slate-900 dark:text-white tracking-wider" data-testid="butterfly-code">
                {result?.code || "—"}
              </p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">
                {result?.already_claimed ? "Already saved for you" : "One-time use · stacks with any other discount"}
              </p>
              <Button
                onClick={handleCopy}
                className="mt-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="butterfly-copy-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy code</>}
              </Button>
            </div>
            <p className="text-[11px] text-center text-slate-400 dark:text-slate-500 mt-3">
              {isAuthenticated
                ? "Saved to your account — auto-applies at checkout."
                : "Save this code — apply it at checkout after you create an account."}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {claiming && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-purple-950/30 backdrop-blur-[2px] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-full p-6 shadow-2xl"
            >
              <ButterflySprite />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export { ButterflySprite };
